import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';
import { vansiteUpdateNotificationStatus } from '../../util/api';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
import {
  TRANSITION_AUTO_ACCEPT,
  TRANSITION_AUTO_CONFIRM_PAYMENT,
  TRANSITION_EXPIRE_PAYMENT_INTENT,
  TRANSITION_PAYMENT_EXPIRE,
} from '../../util/transaction';
import { TRANSITIONS as DEFAULT_TRANSITIONS } from '../../util/transaction.default';
import {
  TRANSITION_EXPIRE_INITIATE_PAYMENT_INTENT,
  TRANSITIONS as DELAYED_TRANSITIONS,
} from '../../util/transaction.delayed';
import { parse } from '../../util/urlHelpers';
import { statusKeysToLastTransitions } from './txStatusFilters';

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const UPDATE_NOTIFICATIONS_REQUEST = 'app/InboxPage/UPDATE_NOTIFICATIONS_REQUEST';
export const UPDATE_NOTIFICATIONS_SUCCESS = 'app/InboxPage/UPDATE_NOTIFICATIONS_SUCCESS';
export const UPDATE_NOTIFICATIONS_ERROR = 'app/InboxPage/UPDATE_NOTIFICATIONS_ERROR';

// ================ Reducer ================ //

const entityRefs = (entities) =>
  entities.map((entity) => ({
    id: entity.id,
    type: entity.type,
  }));

/** @type {import('../../../types/vansite').VanSite.State.InboxPage} */
const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

/**
 *
 * @param state
 * @param action
 */
export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(payload.data.data),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = (response) => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = (e) => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const updateNotificationStatusRequest = () => ({ type: UPDATE_NOTIFICATIONS_REQUEST });
const updateNotificationStatusSuccess = () => ({ type: UPDATE_NOTIFICATIONS_SUCCESS });
const updateNotificationStatusError = (e) => ({
  type: UPDATE_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 25;

export const updateNotificationStatus = (transactionId, userRole) => async (dispatch) => {
  dispatch(updateNotificationStatusRequest());
  try {
    await vansiteUpdateNotificationStatus({ transactionId, userRole, status: true });
    dispatch(updateNotificationStatusSuccess());
    dispatch(fetchCurrentUserNotifications());
  } catch (e) {
    logError(e, 'update-notification-status-failed');
    dispatch(updateNotificationStatusError(e));
  }
};

export const checkTransactions =
  (transactions, onlyFilter, params, search) => (dispatch, getState, sdk) => {
    if (onlyFilter !== 'order') return;
    for (const transaction of transactions) {
      const lastTransition = transaction.attributes.lastTransition;
      if (
        lastTransition === TRANSITION_AUTO_CONFIRM_PAYMENT &&
        transaction.attributes?.protectedData?.stripePaymentIntents?.default?.stripePaymentIntentId
      ) {
        sdk.transactions
          .transition({
            id: transaction.id.uuid,
            transition: TRANSITION_AUTO_ACCEPT,
            params: {
              metadata: {
                providerNotification:
                  lastTransition === 'transition/confirm-payment' ? undefined : true,
              },
            },
          })
          .then(() => {
            dispatch(fetchOrdersOrSales(params, search));
          });
      }
    }
  };

export const fetchOrdersOrSales = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { page: pageParam, status, dir = 'asc' } = parse(search);
  // parse() yields strings; coerce and guard against 0/NaN.
  const page = Number(pageParam) || 1;

  const statusKeys = typeof status === 'string' && status.length ? status.split(',') : [];
  const statusTransitions = statusKeysToLastTransitions(statusKeys);

  let allTransitions = [
    ...new Set([...new Set(DEFAULT_TRANSITIONS), ...new Set(DELAYED_TRANSITIONS)]),
  ].filter((transition) => ![TRANSITION_EXPIRE_INITIATE_PAYMENT_INTENT].includes(transition));

  if (onlyFilter === 'sale') {
    allTransitions = allTransitions.filter(
      (transition) =>
        ![TRANSITION_EXPIRE_PAYMENT_INTENT, TRANSITION_PAYMENT_EXPIRE].includes(transition),
    );
  }

  // Selected status => its transitions; nothing selected => all role transitions.
  const lastTransitions = statusTransitions.length ? statusTransitions : allTransitions;

  // The Marketplace API `/transactions/query` returns results in ASCENDING order
  // (oldest first) and does not honor a `sort` param. The default inbox view is
  // newest-first, so we invert pagination: display page 1 maps to the LAST API
  // page and the items within the fetched page are reversed. `dir === 'asc'`
  // keeps the native ascending order (oldest first) with a direct page fetch.
  const newestFirst = dir !== 'asc';

  const baseParams = {
    only: onlyFilter,
    lastTransitions,
    include: [
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'listing',
      'listing.images',
    ],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'protectedData',
      'metadata',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    per_page: INBOX_PAGE_SIZE,
  };

  const finish = (response, displayPage) => {
    response.data.data = response.data.data.map((tx) => {
      tx.onlyType = onlyFilter;
      return tx;
    });
    if (typeof displayPage === 'number') {
      response.data.meta = { ...response.data.meta, page: displayPage };
    }
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchOrdersOrSalesSuccess(response));
    dispatch(checkTransactions(response.data.data, onlyFilter, params, search));
    dispatch(fetchCurrentUserNotifications());
    return response;
  };

  const onError = (e) => {
    dispatch(fetchOrdersOrSalesError(storableError(e)));
    logError(e, 'fetch-orders-or-sales-failed');
    throw e;
  };

  // Ascending (oldest first): native order, direct page fetch.
  if (!newestFirst) {
    return sdk.transactions
      .query({ ...baseParams, page })
      .then((response) => finish(response))
      .catch(onError);
  }

  // Newest first: a cheap count query gives the page count so we can map the
  // requested display page to the inverted API page, then reverse that page.
  return sdk.transactions
    .query({ only: onlyFilter, lastTransitions, page: 1, per_page: 1 })
    .then((countResponse) => {
      const totalItems = countResponse.data.meta.totalItems;
      const totalPages = Math.max(1, Math.ceil(totalItems / INBOX_PAGE_SIZE));
      const displayPage = Math.min(Math.max(1, page), totalPages);
      const apiPage = totalPages - displayPage + 1;
      return sdk.transactions.query({ ...baseParams, page: apiPage }).then((response) => {
        response.data.data = response.data.data.slice().reverse();
        response.data.meta = { ...response.data.meta, totalItems, totalPages };
        return finish(response, displayPage);
      });
    })
    .catch(onError);
};

export const loadData = (params, search) => (dispatch) => {
  return dispatch(fetchOrdersOrSales(params, search));
};
